import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Paper,
  Button,
  CardContent,
  Typography,
  Divider,
  ButtonBase,
} from '@material-ui/core'
import Tooltip from '@material-ui/core/Tooltip'
import { Skeleton } from '@material-ui/lab'
import { Carousel } from 'react-responsive-carousel'
import mapInfoWindowStyles from './mapInfoWindowStyles'
import {
  setDestination,
  setDirectionRoutes,
  setMarkerInfo,
  setOrigin,
  showDirections,
  setActiveLotId,
} from '../../redux/myMapsSlice'
import {
  formatTimeString,
  scheduleToHumanReadable,
  setPolylineStyle,
} from '../../../../utils/helpers'
import NavigationContent from './NavigationContent'
import IconPicker, {
  CustomIcon,
} from '../../../../../apps/AppComponents/IconPicker/IconPicker'
import CheckIsMobile from '../../../../../../../utils/isMobileHook'
import { logMapEvent } from '../../../../utils/events'
import { PARKINGCHARGINGSTATION } from '../../../../utils/constants'

const MapInfoWindow = ({
  dispatch,
  isMobile,
  parkingLotData,
  filterBy,
  additionalFilters,
  darkMode,
}) => {
  const [campusImage, setCampusImage] = React.useState(null)
  const [showDirectionsButton, updateShowDirectionsButton] =
    React.useState(false)
  const [userPermitHours, setUserPermitHours] = React.useState(null)
  const classes = mapInfoWindowStyles()
  const {
    markerInfo,
    datasetLayer,
    destination,
    directionRoutes,
    directionsRenderer,
    userParkingPermitData,
  } = useSelector((s) => s.maps)
  const handleCloseMarkerInfo = () => {
    dispatch(setMarkerInfo({}))
    dispatch(setOrigin(''))
    dispatch(setDestination(''))
    dispatch(showDirections(false))
    dispatch(setDirectionRoutes([]))
    dispatch(setActiveLotId(null))
    updateShowDirectionsButton(false)
    setUserPermitHours(null)
    datasetLayer.style = ({ feature }) =>
      setPolylineStyle({ feature, parkingLotData, filterBy, additionalFilters })
  }
  const chooseCampusImage = (markerLocation) => {
    let imageRef = null
    switch (markerLocation) {
      case 'NB':
        imageRef = {
          image: './assets/images/nb-campus.jpg',
          label: 'New Brunswick Campus',
        }
        break
      case 'NWK':
        imageRef = {
          image: './assets/images/nwk-campus.jpg',
          label: 'Newark Campus',
        }
        break
      case 'CAM':
        imageRef = {
          image: './assets/images/cmd-campus.jpg',
          label: 'Camden Campus',
        }
        break
      default:
        imageRef = {
          image: './assets/images/nb-campus.jpg',
          label: 'New Brunswick Campus',
        }
    }
    setCampusImage(imageRef)
  }
  const updateNavigationRoute = (index) => {
    directionsRenderer.setRouteIndex(index)
  }
  const handleUpdateShowDirections = () => {
    updateShowDirectionsButton(!showDirectionsButton)
  }
  React.useEffect(() => {
    chooseCampusImage(markerInfo?.address?.regionCode)
    if (Object.keys(userParkingPermitData)?.length > 0) {
      const matchingGarage = userParkingPermitData?.garageIdInfo?.find(
        (garageObj) => garageObj?.garageId === markerInfo?.baseId,
      )
      // This will be the array of rules, or an empty array if no garage is found
      const hoursArray = matchingGarage ? matchingGarage.accessRules : []
      setUserPermitHours(hoursArray)
    }
    return () => {
      setUserPermitHours(null)
    }
  }, [markerInfo, userParkingPermitData])
  if (isMobile) return null // check BottomSheetMobileInfoWindow for mobile info window
  return (
    <>
      {markerInfo && Object.keys(markerInfo).length !== 0 ? (
        <Paper className={classes.mapInfoContainer} variant="outlined">
          <CardContent className={classes.mapInfoContainerContent}>
            <div className={classes.mapInfoCardContentContainer}>
              <div className={classes.campusImageContainer}>
                {campusImage ? (
                  <ParkingLotImageCarousel lot={markerInfo} classes={classes} />
                ) : (
                  <Skeleton width={250} height={154} />
                )}
              </div>
              <Typography variant="subtitle1" className={classes.campusText}>
                {markerInfo?.propertyName}
              </Typography>
              <Typography variant="body1">
                {`${markerInfo?.address?.address1}, ${markerInfo?.address?.cityCode}, NJ `}
              </Typography>
              <Typography variant="body1">
                {markerInfo?.address?.campus}
              </Typography>
              {userPermitHours && filterBy?.includes('permit') ? (
                <div className={classes.hoursContainer}>
                  <Typography
                    variant="h4"
                    component="h1"
                    style={{ fontWeight: 'bold', marginBottom: 5 }}
                  >
                    Permitted Hours
                  </Typography>
                  {userPermitHours?.map((hours, index) => (
                    // eslint-disable-next-line
                    <div key={index} style={{ marginBottom: 10 }}>
                      <Typography variant="body2">
                        {scheduleToHumanReadable(hours)}
                      </Typography>
                      <Typography variant="body2">{hours.name}</Typography>
                      <Typography variant="body2">
                        {formatTimeString(hours.enter)} to{' '}
                        {formatTimeString(hours.exit)}
                      </Typography>
                    </div>
                  ))}
                </div>
              ) : null}
              {markerInfo?.empHours && filterBy?.includes('Faculty/Staff') ? (
                <div className={classes.hoursContainer}>
                  <Typography
                    variant="h4"
                    component="h1"
                    style={{ fontWeight: 'bold', marginBottom: 5 }}
                  >
                    Employee Hours
                  </Typography>
                  <Typography variant="body2">
                    {markerInfo?.empHours.split('\n').map((line) => (
                      <React.Fragment key={line}>
                        <span>{line}</span>
                        <br />
                      </React.Fragment>
                    ))}
                  </Typography>
                </div>
              ) : null}
              <MapInfoNote classes={classes} markerInfo={markerInfo} />
              <MapInfoTable
                classes={classes}
                markerInfo={markerInfo}
                darkMode={darkMode}
              />
            </div>
            <Divider
              orientation="horizontal"
              className={classes.mapInfoDividerStyle}
            />
            <NavigationContent
              dispatch={dispatch}
              classes={classes}
              showDirectionsButton={showDirectionsButton}
              handleUpdateShowDirections={handleUpdateShowDirections}
              destination={destination}
              directionRoutes={directionRoutes}
              updateNavigationRoute={updateNavigationRoute}
              updateShowDirectionsButton={updateShowDirectionsButton}
              darkMode={darkMode}
              propertyName={markerInfo.propertyName}
            />
            <Box className={classes.mapInfoButtonContainer}>
              <Button
                color="primary"
                variant="contained"
                size="medium"
                className={classes.mapInfoButton}
                onClick={handleCloseMarkerInfo}
              >
                Close
              </Button>
            </Box>
          </CardContent>
        </Paper>
      ) : null}
    </>
  )
}

export const MapInfoNote = ({ markerInfo }) => {
  const classes = mapInfoWindowStyles()
  return (
    <div className={classes.noteContainer}>
      <Typography variant="subtitle1" component="span">
        {markerInfo.note}
      </Typography>
    </div>
  )
}

export const MapInfoTable = ({ markerInfo, classes, darkMode }) => {
  const isMobile = CheckIsMobile()
  const iconSize = isMobile ? 25 : 30
  const dispatch = useDispatch()
  const handleChargingStationClick = () => {
    logMapEvent(dispatch, PARKINGCHARGINGSTATION, markerInfo?.evChargeInfo)
  }
  return (
    <div className={classes.mapInfoTableContainer}>
      <Tooltip
        title={<Typography variant="subtitle2">Total Spaces</Typography>}
        placement="bottom"
        arrow
      >
        <ButtonBase className={classes.mapInfoTableContentContainer}>
          <IconPicker iconName={['fas', 'table-cells']} size="2x" />
          <Typography variant="subtitle1" component="p">
            {markerInfo.totalSpaces}
          </Typography>
        </ButtonBase>
      </Tooltip>
      <div className={classes.mapInfoTableContentContainer}>
        <Tooltip
          title={<Typography variant="subtitle2">General Spaces</Typography>}
          placement="bottom"
          arrow
        >
          <ButtonBase className={classes.mapInfoTableContentContainer}>
            <CustomIcon
              imgSrc={
                darkMode
                  ? './assets/images/svgs/generalspaces-darkmode.svg'
                  : './assets/images/svgs/generalspaces.svg'
              }
              size={iconSize}
            />
            <Typography variant="subtitle1" component="p">
              {markerInfo.generalAvailable}
            </Typography>
          </ButtonBase>
        </Tooltip>
      </div>
      <div className={classes.mapInfoTableContentContainer}>
        <Tooltip
          title={<Typography variant="subtitle2">Visitor Spaces</Typography>}
          placement="bottom"
          arrow
        >
          <ButtonBase className={classes.mapInfoTableContentContainer}>
            <IconPicker iconName={['fas', 'id-badge']} size="2x" />
            <Typography variant="subtitle1" component="p">
              {markerInfo.visitor}
            </Typography>
          </ButtonBase>
        </Tooltip>
      </div>
      <div className={classes.mapInfoTableContentContainer}>
        <Tooltip
          title={
            <Typography variant="subtitle2">Handicapped Spaces</Typography>
          }
          placement="bottom"
          arrow
        >
          <ButtonBase className={classes.mapInfoTableContentContainer}>
            <IconPicker iconName={['fas', 'wheelchair']} size="2x" />
            <Typography variant="subtitle1" component="p">
              {markerInfo.handicapped}
            </Typography>
          </ButtonBase>
        </Tooltip>
      </div>
      <div className={classes.mapInfoTableContentContainer}>
        <Tooltip
          title={
            <Typography variant="subtitle2">
              EV Charging Station Spaces
            </Typography>
          }
          placement="bottom"
          arrow
        >
          <ButtonBase
            className={classes.mapInfoTableContentContainer}
            component={markerInfo?.evChargeInfo ? 'a' : 'button'}
            href={markerInfo?.evChargeInfo ? markerInfo?.evChargeInfo : null}
            target="_blank"
            onClick={handleChargingStationClick}
          >
            <IconPicker
              iconName={['fas', 'charging-station']}
              color={
                // eslint-disable-next-line
                markerInfo?.evChargeInfo
                  ? darkMode
                    ? '#91DAFF'
                    : '#4183C4'
                  : 'inherit'
              }
              size="2x"
            />
            <Typography variant="subtitle1" component="p">
              {markerInfo?.evChargeInfo ? (
                <>
                  <Typography
                    variant="subtitle1"
                    component="a"
                    href={markerInfo?.evChargeInfo}
                    target="_blank"
                    className={classes.evChargingIconLabel}
                    onClick={handleChargingStationClick}
                  >
                    {markerInfo.evCharging}
                  </Typography>
                </>
              ) : (
                <Typography variant="subtitle1" component="p">
                  {markerInfo.evCharging}
                </Typography>
              )}
            </Typography>
          </ButtonBase>
        </Tooltip>
      </div>
    </div>
  )
}

export const ParkingLotImageCarousel = ({ lot, classes }) => {
  const [parkingImages, updateParkingImages] = React.useState([])
  React.useEffect(() => {
    updateParkingImages([])
    if (lot?.photos) {
      updateParkingImages(lot?.photos)
    }
  }, [lot])
  return (
    <div className={classes.campusImageContainer}>
      {parkingImages?.length > 0 ? (
        <Carousel
          showArrows
          autoPlay
          infiniteLoop
          showThumbs={false}
          transitionTime={1000}
          interval={8000}
        >
          {parkingImages?.map((parkingImage) => (
            <img
              key={parkingImage}
              src={parkingImage}
              alt={lot.shortName}
              className={classes.campusImage}
              style={{ height: 200 }}
            />
          ))}
        </Carousel>
      ) : null}
    </div>
  )
}

export default MapInfoWindow
